import type { UserLocationDetails } from "../../Dashboard/dashboard.component";
import type { LocationInventory } from "../../FloorManager/typings/location-inventory";
import type { LocationUsage } from "../../Reports/typings/reports.types";

/** Backend sentinel: location name when the user booked mobile-working. */
export const MOBILE_WORKING_LOCATION_NAME = "Mobile Working";

/** Backend sentinel: location name placeholder when no booking was made. */
export const NO_BOOKING_TIME_LOCATION_NAME = "No Booking Time";

export const getFavoriteLocationKey = (location: LocationUsage): string =>
  `${location.locationInventoryId}-${location.locationName}`;

/** Physical sites only — mobile working is a work status, not a favorite location. */
export const isDashboard2FavoriteLocation = (usage: LocationUsage): boolean =>
  !usage.isMobileWorking && usage.locationName !== MOBILE_WORKING_LOCATION_NAME;

export const getDashboard2FavoriteLocations = (
  userReport: UserLocationDetails[] | undefined,
  showBookingStatistics: boolean
): LocationUsage[] =>
  userReport?.[0]?.duration?.filter(
    usage =>
      isDashboard2FavoriteLocation(usage) &&
      (usage.locationName !== NO_BOOKING_TIME_LOCATION_NAME || showBookingStatistics)
  ) ?? [];

/** Mobile-working site from user report — used for status-sheet mobile bookings (not a favorite). */
export const getDashboard2MobileWorkingLocation = (
  userReport: UserLocationDetails[] | undefined
): LocationUsage | undefined =>
  userReport?.[0]?.duration?.find(
    usage => usage.isMobileWorking || usage.locationName === MOBILE_WORKING_LOCATION_NAME
  );

const isMobileWorkingInventory = (inventory: LocationInventory): boolean =>
  inventory.isMobileWorking || inventory.name === MOBILE_WORKING_LOCATION_NAME;

/** Fallback when usage report has no mobile-working row (e.g. no mobile bookings last month). */
export const getDashboard2MobileWorkingLocationFromInventories = (
  locations: readonly LocationInventory[] | undefined
): LocationUsage | undefined => {
  const inventory = locations?.find(isMobileWorkingInventory);
  if (!inventory) {
    return undefined;
  }

  return {
    locationInventoryId: inventory.id,
    locationName: inventory.name,
    locationColor: inventory.color,
    absoluteDuration: 0,
    relativeDuration: 0,
    isMobileWorking: true,
    picturePath: inventory.picturePath ?? undefined
  };
};

export const resolveDashboard2MobileWorkingLocation = (
  userReport: UserLocationDetails[] | undefined,
  companyLocations: readonly LocationInventory[] | undefined
): LocationUsage | undefined =>
  getDashboard2MobileWorkingLocation(userReport) ??
  getDashboard2MobileWorkingLocationFromInventories(companyLocations);

/**
 * A site from the company catalog (`/location/companies-schedule`) or a full location inventory.
 * Booking-settings responses use `isAllowedForBooking`; inventories use `allowForBooking`.
 */
export type Dashboard2OfficeLocationCandidate = {
  /** Location inventory id. `0` is the report placeholder, not a site. */
  id: number;
  name: string;
  isMobileWorking?: boolean;
  /** Inventory flag. `false` means the site is closed for booking. */
  allowForBooking?: boolean;
  /** Companies-schedule flag. Same meaning as `allowForBooking`. */
  isAllowedForBooking?: boolean;
  color?: string;
  picturePath?: string | null;
};

/** Last-month favorite that can be sent as `locationInventoryId` on a workplace quick book. */
export const isUsableDashboard2OfficeLocation = (
  location: LocationUsage | undefined
): location is LocationUsage => {
  if (!location || location.locationInventoryId <= 0) {
    return false;
  }
  if (location.isMobileWorking || location.locationName === MOBILE_WORKING_LOCATION_NAME) {
    return false;
  }
  return location.locationName !== NO_BOOKING_TIME_LOCATION_NAME;
};

const isBookableOfficeLocation = (location: Dashboard2OfficeLocationCandidate): boolean => {
  if (location.id <= 0) {
    return false;
  }
  if (location.isMobileWorking || location.name === MOBILE_WORKING_LOCATION_NAME) {
    return false;
  }
  if (location.name === NO_BOOKING_TIME_LOCATION_NAME) {
    return false;
  }
  if (location.allowForBooking === false || location.isAllowedForBooking === false) {
    return false;
  }
  return true;
};

const toOfficeLocationUsage = (location: Dashboard2OfficeLocationCandidate): LocationUsage => ({
  locationInventoryId: location.id,
  locationName: location.name,
  locationColor: location.color ?? "",
  absoluteDuration: 0,
  relativeDuration: 0,
  isMobileWorking: false,
  picturePath: location.picturePath ?? undefined
});

/**
 * Why an office status change did not receive a site.
 * `preferred-unavailable` means saved offices were checked and none can be booked.
 * Another office is not chosen.
 */
export type Dashboard2OfficeLocationFallback =
  | { kind: "ready"; location: LocationUsage }
  | { kind: "preferred-unavailable" }
  | { kind: "unavailable" };

/**
 * First site the person can book when the usage report has no office favorite.
 * Preferred locations win, in the order saved in booking settings.
 * The quick-book API then picks the first available workplace at that site.
 */
export const getDashboard2FirstBookableOfficeLocation = (
  locations: readonly Dashboard2OfficeLocationCandidate[] | undefined,
  preferredLocationIds: readonly string[] | undefined
): LocationUsage | undefined => {
  const bookable = (locations ?? []).filter(isBookableOfficeLocation);
  if (bookable.length === 0) {
    return undefined;
  }

  const preferredIds = (preferredLocationIds ?? []).filter(id => id.trim() !== "");
  if (preferredIds.length > 0) {
    const byId = new Map(bookable.map(location => [String(location.id), location]));
    const match = preferredIds.map(id => byId.get(id)).find(location => location != null);
    return match ? toOfficeLocationUsage(match) : undefined;
  }

  const first = bookable[0];
  if (first) {
    return toOfficeLocationUsage(first);
  }
  return undefined;
};

/**
 * Favorite-office fallback. Saved offices are the only candidates when the person has any.
 * When those offices are gone, the result says so and does not pick another site.
 */
export const resolveDashboard2OfficeLocationFallback = (
  locations: readonly Dashboard2OfficeLocationCandidate[] | undefined,
  preferredLocationIds: readonly string[] | undefined
): Dashboard2OfficeLocationFallback => {
  const preferredIds = (preferredLocationIds ?? []).filter(id => id.trim() !== "");
  const location = getDashboard2FirstBookableOfficeLocation(locations, preferredIds);
  if (location) {
    return { kind: "ready", location };
  }
  if (preferredIds.length > 0) {
    return { kind: "preferred-unavailable" };
  }
  return { kind: "unavailable" };
};
